import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, ArrowRight, Search, X, Wallet, Filter, ChevronDown, ExternalLink, Info, Sparkles,
  IndianRupee, BadgeCheck, Landmark,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LexiTrigger from '../components/lexi/LexiTrigger';
import { normalizeEntrepreneurship } from '../lib/entrepreneurshipNormalizer';
import { loadContent, isLocked } from '../lib/contentLoader';
import useCanSeeCounts from '../hooks/useCanSeeCounts';
import ContentLockedCard from '../components/ContentLockedCard';
const CAT_VIS = {
  'Food': { bg: 'bg-orange-50', text: 'text-orange-700' },
  'Food/Health': { bg: 'bg-rose-50', text: 'text-rose-700' },
  'Food Processing': { bg: 'bg-amber-50', text: 'text-amber-700' },
  'Green Energy': { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'EV/Mobility': { bg: 'bg-sky-50', text: 'text-sky-700' },
  'Digital': { bg: 'bg-violet-50', text: 'text-violet-700' },
  'Services': { bg: 'bg-pink-50', text: 'text-pink-700' },
  'Repair/Retail': { bg: 'bg-cyan-50', text: 'text-cyan-700' },
  'Services/Manufacturing': { bg: 'bg-stone-50', text: 'text-stone-700' },
  'Education': { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  'Manufacturing': { bg: 'bg-zinc-50', text: 'text-zinc-700' },
};
const catVis = (c) => CAT_VIS[c] || { bg: 'bg-gray-100', text: 'text-gray-700' };

const EntrepreneurshipPage = () => {
  const canSeeCounts = useCanSeeCounts();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ category: null, mode: null });
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [schemeRegion, setSchemeRegion] = useState('All');
  const [schemeQuery, setSchemeQuery] = useState('');

  useEffect(() => {
    loadContent('entrepreneurship.json')
      .then((d) => {
        if (isLocked(d)) { setLocked(true); setLoading(false); return; }
        if (d) setPkg(normalizeEntrepreneurship(d));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!pkg) return [];
    const q = query.trim().toLowerCase();
    return pkg.pathways.filter(p => {
      if (filters.category && p.category !== filters.category) return false;
      if (filters.mode && p.mode !== filters.mode) return false;
      if (!q) return true;
      const hay = `${p.title} ${(p.bestFor || []).join(' ')} ${p.category}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pkg, query, filters]);

  const filterOptions = useMemo(() => {
    if (!pkg) return { categories: [], modes: [] };
    return {
      categories: [...new Set(pkg.pathways.map(p => p.category))],
      modes: [...new Set(pkg.pathways.map(p => p.mode))],
    };
  }, [pkg]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: f[k] === v ? null : v }));
  const clearAll = () => { setFilters({ category: null, mode: null }); setQuery(''); };
  const activeCount = Object.values(filters).filter(Boolean).length + (query ? 1 : 0);

  const normRegion = (r) => (/central|india/i.test(r || '') ? 'Central' : r);

  const schemesFiltered = useMemo(() => {
    if (!pkg) return [];
    const q = schemeQuery.trim().toLowerCase();
    return pkg.schemes.filter(s => {
      if (schemeRegion !== 'All' && normRegion(s.region) !== schemeRegion) return false;
      if (!q) return true;
      const hay = `${s.title} ${s.snapshot || ''} ${(s.supportTypes || []).join(' ')} ${(s.keywords || []).join(' ')} ${(s.bestFor || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pkg, schemeRegion, schemeQuery]);

  const regionCounts = useMemo(() => {
    if (!pkg) return {};
    const c = { All: pkg.schemes.length };
    pkg.schemes.forEach(s => { const r = normRegion(s.region); c[r] = (c[r] || 0) + 1; });
    return c;
  }, [pkg]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50"><Loader2 className="animate-spin text-emerald-600" size={32} /></div>;
  if (!pkg) return <div className="min-h-screen flex items-center justify-center"><p>Could not load.</p></div>;

  const previewCards = pkg.emergentStructure?.homepagePreviewCards || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="entrepreneurship-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 md:px-6 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(16,185,129,0.18) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.18) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
        <div className="max-w-5xl mx-auto text-center relative">
          <Badge className="bg-emerald-100 text-emerald-800 mb-3 font-bold">OwnPath Govt Entrepreneurship Hub · {pkg.pathways.length} Pathways · {pkg.schemes.length} Schemes</Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">Start small. Test fast. Grow with government support.</h1>
          <p className="text-gray-700 max-w-3xl mx-auto text-sm md:text-base leading-relaxed mb-6">Practical, student-friendly business pathways for Andhra Pradesh & Telangana — with verified central + state MSME and startup schemes.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="cta-find-path" onClick={() => document.getElementById('pathway-grid')?.scrollIntoView({ behavior: 'smooth' })} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md">
              <Search size={16} className="mr-2" /> Find a pathway
            </Button>
            <Button data-testid="cta-schemes" onClick={() => document.getElementById('schemes-grid')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <Wallet size={16} className="mr-2" /> Explore schemes
            </Button>
          </div>
          <div className="mt-5 flex justify-center">
            <LexiTrigger
              variant="pill"
              prefill="I have a small budget (₹5,000–₹25,000) and want to start something. Match me to one pathway and tell me which govt scheme fits."
              label="Tell Lexi your budget → get matched"
              testId="entrep-lexi-trigger"
            />
          </div>
        </div>
      </section>

      {/* Preview cards */}
      {previewCards.length > 0 && (
        <section className="px-4 md:px-6 -mt-6 relative z-10">
          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="preview-cards">
            {previewCards.map((c, i) => (
              <div key={i} className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
                <div className="text-2xl mb-1.5">{c.emoji}</div>
                <p className="text-sm font-bold text-gray-900 mb-1">{c.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Government Schemes (TOP) */}
      <section id="schemes-grid" className="px-4 md:px-6 py-12 mt-6 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <Badge className="bg-amber-100 text-amber-800 mb-2">Verified Government Schemes</Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{pkg.schemes.length} Schemes — Central + AP + Telangana</h2>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">Subsidies, credit-linked support, MSME registration, and startup ecosystem programs.</p>
          </div>
          {/* Always-visible disclaimer at the top */}
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-2xl flex gap-3" data-testid="schemes-disclaimer">
            <Info className="text-yellow-700 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-yellow-900 leading-relaxed">
              <strong>Important:</strong> Scheme eligibility, subsidy amounts, deadlines, and application process may change. Always verify from the official government portal before applying.
            </p>
          </div>

          {/* Region tabs + scheme search */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1" data-testid="scheme-region-tabs">
              {['All', 'Central', 'Andhra Pradesh', 'Telangana'].map((r) => {
                const vis = REGION_VIS[r] || {};
                const active = schemeRegion === r;
                return (
                  <button
                    key={r}
                    data-testid={`scheme-region-${r.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => setSchemeRegion(r)}
                    className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full border-2 transition-all ${
                      active
                        ? (vis.tabActive || 'bg-gray-900 border-gray-900 text-white')
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {r === 'Central' && <Landmark size={13} />}
                    {r === 'All' ? 'All schemes' : r}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/25' : 'bg-gray-100 text-gray-600'}`}>{regionCounts[r] || 0}</span>
                  </button>
                );
              })}
            </div>
            <div className="relative md:ml-auto md:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="scheme-search-input"
                value={schemeQuery}
                onChange={(e) => setSchemeQuery(e.target.value)}
                placeholder="Search schemes (subsidy, loan, women...)"
                className="pl-9 pr-9 bg-white"
              />
              {schemeQuery && <button onClick={() => setSchemeQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
            </div>
          </div>

          {schemesFiltered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500 mb-3">No schemes match your search.</p>
              <Button variant="outline" size="sm" onClick={() => { setSchemeQuery(''); setSchemeRegion('All'); }}>Clear scheme filters</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {schemesFiltered.map(s => <SchemeCard key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </section>

      {/* Business Ideas — title strip */}
      <section className="px-4 md:px-6 pt-12 pb-2">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-emerald-100 text-emerald-800 mb-2">Business Ideas — Pick One to Start</Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{pkg.pathways.length} student-friendly pathways</h2>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">Juice stalls, EV charging, solar repair, tailoring & more. Filter by category or budget to find the right fit.</p>
        </div>
      </section>

      {/* Filter bar */}
      <div className="lxt-sticky-nav z-30 bg-white/85 backdrop-blur-md border-b border-gray-200 mt-4">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 space-y-3">
          <div className="relative max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pathways (juice, EV charging, solar, tailoring...)"
              className="pl-9 pr-9"
            />
            {query && <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1">Category</span>
            {filterOptions.categories.map(c => (
              <FilterChip key={c} label={c} active={filters.category === c} onClick={() => setFilter('category', c)} testid={`f-cat-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} />
            ))}
          </div>

          <button onClick={() => setShowAllFilters(!showAllFilters)} data-testid="more-filters-btn" className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
            <Filter size={11} /> {showAllFilters ? 'Hide' : 'Show'} more filters {activeCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-1">{activeCount}</Badge>}
            <ChevronDown size={11} className={`transition-transform ${showAllFilters ? 'rotate-180' : ''}`} />
          </button>

          {showAllFilters && (
            <div className="space-y-2 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1">Mode</span>
                {filterOptions.modes.map(m => (
                  <FilterChip key={m} label={m} active={filters.mode === m} onClick={() => setFilter('mode', m)} testid={`f-mode-${m.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} />
                ))}
              </div>
              {activeCount > 0 && <button onClick={clearAll} data-testid="clear-filters" className="text-[11px] text-red-600 hover:text-red-800 inline-flex items-center gap-1"><X size={11} /> Clear all filters</button>}
            </div>
          )}
        </div>
      </div>

      {/* Result count */}
      <section className="px-4 md:px-6 pt-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs text-gray-500" data-testid="result-count">
            {canSeeCounts
              ? <>Showing <strong>{filtered.length}</strong> of {pkg.pathways.length} pathways</>
              : (filtered.length === 0 ? 'No pathways match your filters' : 'Showing matching pathways')}
          </p>
        </div>
      </section>

      {/* Pathway grid (BELOW) */}
      <section id="pathway-grid" className="px-4 md:px-6 py-6">
        <div className="max-w-6xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 mb-3">No pathways match your filters.</p>
              <Button onClick={clearAll} variant="outline" size="sm">Clear filters</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pathway-grid">
              {filtered.map(p => <PathwayCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-4 md:px-6 pb-12">
        <div className="max-w-5xl mx-auto p-5 bg-yellow-50 border border-yellow-200 rounded-2xl">
          <div className="flex items-start gap-2">
            <Info className="text-yellow-700 flex-shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-xs font-bold text-yellow-900 mb-1">Important Note</p>
              <p className="text-xs text-yellow-800 leading-relaxed">{pkg.metadata?.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FilterChip = ({ label, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-all border ${
      active ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50'
    }`}
  >{label}</button>
);

const PathwayCard = ({ p }) => {
  const v = catVis(p.category);
  const chips = p.previewCard?.chips || [];
  return (
    <Link
      to={`/explore/entrepreneurship/${p.id}`}
      data-testid={`pathway-card-${p.id}`}
      className="group p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-emerald-400 hover:shadow-lg transition-all flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 flex items-center justify-center text-3xl">{p.emoji}</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">{p.title}</h3>
          <div className="flex flex-wrap gap-1 mt-1.5">
            <Badge className={`${v.bg} ${v.text} border-0 text-[10px]`}>{p.category}</Badge>
            <Badge className="bg-gray-100 text-gray-700 border-0 text-[10px]">{p.mode}</Badge>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{p.previewCard?.subtext}</p>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.slice(0, 4).map((c, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">{c}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <Wallet size={12} className="text-emerald-600" />
          <span className="text-[11px] font-bold text-emerald-700">{p.startBudget}</span>
        </div>
        <span className="text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-1">
          <Sparkles size={11} />Read full preview
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

const REGION_VIS = {
  'All':            { tabActive: 'bg-gray-900 border-gray-900 text-white' },
  'Andhra Pradesh': { bar: 'from-amber-400 to-orange-500', badge: 'bg-amber-100 text-amber-800 border-amber-200', money: 'bg-amber-50 border-amber-200 text-amber-900', hover: 'hover:border-amber-400', tabActive: 'bg-amber-600 border-amber-600 text-white' },
  'Telangana':      { bar: 'from-emerald-400 to-teal-500', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', money: 'bg-emerald-50 border-emerald-200 text-emerald-900', hover: 'hover:border-emerald-400', tabActive: 'bg-emerald-600 border-emerald-600 text-white' },
  'Central':        { bar: 'from-indigo-400 to-blue-500', badge: 'bg-indigo-100 text-indigo-800 border-indigo-200', money: 'bg-indigo-50 border-indigo-200 text-indigo-900', hover: 'hover:border-indigo-400', tabActive: 'bg-indigo-600 border-indigo-600 text-white' },
};
const regionVis = (r) => REGION_VIS[r] || REGION_VIS['Central'];

const SchemeCard = ({ s }) => {
  const primaryLink = s.officialGovernmentLinks?.[0]?.url;
  const vis = regionVis(s.region);
  const keyFigure = Array.isArray(s.keyFigures) ? s.keyFigures[0] : null;
  return (
    <div data-testid={`scheme-${s.id}`} className={`group bg-white rounded-2xl border-2 border-gray-200 ${vis.hover} hover:shadow-lg transition-all flex flex-col overflow-hidden`}>
      <div className={`h-1.5 w-full bg-gradient-to-r ${vis.bar}`} />
      <div className="p-5 flex flex-col flex-1">
        <Link to={`/explore/entrepreneurship/scheme/${s.id}`} className="block flex-1">
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <Badge className={`${vis.badge} border text-[9px] font-bold uppercase`}>{s.emoji} {s.region}</Badge>
            {s.verified && (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] inline-flex items-center gap-0.5">
                <BadgeCheck size={10} /> Verified
              </Badge>
            )}
          </div>
          <h3 className="text-base font-bold text-gray-900 leading-tight">{s.title}</h3>
          <p className="text-[10px] text-gray-500 mt-0.5 mb-2.5">{s.type}</p>
          {keyFigure && (
            <div className={`flex items-start gap-2 rounded-xl border p-2.5 mb-2.5 ${vis.money}`} data-testid={`scheme-keyfigure-${s.id}`}>
              <IndianRupee size={14} className="shrink-0 mt-0.5" />
              <p className="text-xs font-bold leading-snug">{keyFigure}</p>
            </div>
          )}
          <p className="text-xs text-gray-700 mb-2.5 line-clamp-2 leading-relaxed">{s.snapshot}</p>
          {s.supportTypes?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {s.supportTypes.slice(0, 3).map((t, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full border border-gray-200">{t}</span>
              ))}
              {s.supportTypes.length > 3 && <span className="text-[10px] px-1.5 py-0.5 text-gray-400">+{s.supportTypes.length - 3}</span>}
            </div>
          )}
        </Link>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
          <Link
            to={`/explore/entrepreneurship/scheme/${s.id}`}
            className="text-[11px] font-semibold text-gray-700 group-hover:text-gray-900 inline-flex items-center gap-1"
          >
            Full details <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          {primaryLink && (
            <a
              href={primaryLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`gov-portal-${s.id}`}
              className="text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 transition-colors"
            >
              Verify / Apply <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntrepreneurshipPage;
